// Keyboard-shortcut usage counters. PII-free: no event, no payload,
// no timing — just a per-action incrementing integer that accumulates
// across a session. The `getSnapshot()` shape is suitable for
// admin-panel telemetry later.
//
// Persistence: localStorage ("shortcut-analytics-v1") so counts
// survive reloads. Reset via `resetShortcutAnalytics()` (called from
// tests + a dev-only debug hook).

export type ShortcutAction = 'save' | 'undo' | 'redo' | 'delete' | 'escape' | 'help';

const STORAGE_KEY = 'shortcut-analytics-v1';

function readStore(): Record<string, number> {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    // Defensive: ignore corrupt shapes without throwing.
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, number>;
    }
    return {};
  } catch { return {}; }
}

function writeStore(store: Record<string, number>): void {
  try { globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(store)); } catch { /* ignored */ }
}

/**
 * Bump the usage counter for a shortcut action. Safe to call in
 * environments without localStorage (returns silently).
 */
export function bumpShortcutUsage(action: ShortcutAction): void {
  const store = readStore();
  store[action] = (store[action] ?? 0) + 1;
  writeStore(store);
}

/**
 * Read-only snapshot of counters. Returns zero for actions that have
 * never been triggered so consumers don't need to null-check.
 */
export function getShortcutAnalytics(): Record<ShortcutAction, number> {
  const store = readStore();
  const actions: ShortcutAction[] = ['save', 'undo', 'redo', 'delete', 'escape', 'help'];
  const out = {} as Record<ShortcutAction, number>;
  for (const a of actions) out[a] = store[a] ?? 0;
  return out;
}

/** Sum of all counters — useful for "total shortcuts used" displays. */
export function totalShortcutUsage(): number {
  const snap = getShortcutAnalytics();
  return Object.values(snap).reduce((acc, n) => acc + n, 0);
}

/** List unused shortcuts (counter still 0). Helps the a11y audit. */
export function unusedShortcuts(): ShortcutAction[] {
  const snap = getShortcutAnalytics();
  return (Object.entries(snap) as Array<[ShortcutAction, number]>)
    .filter(([, n]) => n === 0)
    .map(([a]) => a);
}

/** Clear all counters. Intended for user-triggered "reset stats" + tests. */
export function resetShortcutAnalytics(): void {
  try { globalThis.localStorage?.removeItem(STORAGE_KEY); } catch { /* ignored */ }
}
